import React, { useState, useEffect } from 'react';
import { API_URLS } from '../../../config/api';
import { Plus, Edit, Trash2, Save, X, Globe, DollarSign, RefreshCw } from 'lucide-react';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Badge } from '../../ui/badge';

const AdminDomainPrices = () => {
  const [prices, setPrices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [formData, setFormData] = useState({
    tld: '',
    registration_price: '',
    original_price: '',
    renewal_price: '',
    transfer_price: '',
    currency: 'USD',
    is_promo: false,
    is_active: true,
    sort_order: 0
  });

  useEffect(() => {
    loadPrices();
  }, []);

  const loadPrices = async () => {
    try {
      setLoading(true);
      const response = await fetch(API_URLS.ADMIN.DOMAIN_PRICES);
      const data = await response.json();
      
      if (data.status === 'success') {
        setPrices(data.data || []);
      } else {
        setError(data.message || 'Domain fiyatları yüklenemedi');
      }
    } catch (err) {
      setError('Yükleme hatası: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const url = editingId 
        ? API_URLS.ADMIN.UPDATE_DOMAIN_PRICE(editingId)
        : API_URLS.ADMIN.CREATE_DOMAIN_PRICE;
      
      const response = await fetch(url, {
        method: editingId ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...formData,
          registration_price: parseFloat(formData.registration_price),
          original_price: formData.original_price ? parseFloat(formData.original_price) : null,
          renewal_price: parseFloat(formData.renewal_price),
          transfer_price: parseFloat(formData.transfer_price),
          is_promo: formData.is_promo ? 1 : 0,
          is_active: formData.is_active ? 1 : 0,
          sort_order: parseInt(formData.sort_order)
        })
      });
      
      const data = await response.json();
      
      if (data.status === 'success') {
        await loadPrices();
        resetForm();
        alert(editingId ? 'Domain fiyatı güncellendi' : 'Domain fiyatı eklendi');
      } else {
        alert('Hata: ' + data.message);
      }
    } catch (err) {
      alert('İşlem hatası: ' + err.message);
    }
  };

  const handleEdit = (price) => {
    setEditingId(price.id);
    setFormData({
      tld: price.tld,
      registration_price: price.registration_price,
      original_price: price.original_price || '',
      renewal_price: price.renewal_price,
      transfer_price: price.transfer_price,
      currency: price.currency || 'USD',
      is_promo: !!price.is_promo,
      is_active: !!price.is_active,
      sort_order: price.sort_order || 0
    });
    setShowAddForm(true);
  };

  const handleDelete = async (id) => {
    if (!confirm('Bu domain fiyatını silmek istediğinizden emin misiniz?')) return;
    
    try {
      const response = await fetch(API_URLS.ADMIN.DELETE_DOMAIN_PRICE(id), {
        method: 'DELETE'
      });
      const data = await response.json();
      
      if (data.status === 'success') {
        await loadPrices();
        alert('Domain fiyatı silindi');
      } else {
        alert('Silme hatası: ' + data.message);
      }
    } catch (err) {
      alert('Silme hatası: ' + err.message);
    }
  };

  const toggleStatus = async (id) => {
    try {
      const response = await fetch(API_URLS.ADMIN.TOGGLE_DOMAIN_PRICE_STATUS(id), {
        method: 'PATCH'
      });
      const data = await response.json();
      
      if (data.status === 'success') {
        await loadPrices();
      } else {
        alert('Durum değiştirme hatası: ' + data.message);
      }
    } catch (err) {
      alert('Durum değiştirme hatası: ' + err.message);
    }
  };

  const resetForm = () => {
    setEditingId(null);
    setShowAddForm(false);
    setFormData({
      tld: '',
      registration_price: '',
      original_price: '',
      renewal_price: '',
      transfer_price: '',
      currency: 'USD',
      is_promo: false,
      is_active: true,
      sort_order: 0
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-12">
        <div className="text-center">
          <RefreshCw className="w-12 h-12 animate-spin text-blue-600 mx-auto mb-4" />
          <p className="text-gray-600">Yükleniyor...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 overflow-x-hidden">
      <div className="mb-4 sm:mb-6">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div className="w-full sm:w-auto">
            <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-900 mb-2 break-words">Domain Fiyatları</h1>
            <p className="text-sm sm:text-base text-gray-600 break-words">Alan adı uzantılarının fiyatlarını yönetin</p>
          </div>
          <Button onClick={() => setShowAddForm(true)} className="bg-blue-600 hover:bg-blue-700 w-full sm:w-auto">
            <Plus className="w-4 h-4 mr-2" />
            Yeni Domain Fiyatı
          </Button>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4 sm:mb-6 text-sm break-words">
          {error}
        </div>
      )}

      {/* Add/Edit Form */}
      {showAddForm && (
        <Card className="mb-4 sm:mb-6">
          <CardHeader className="p-4 sm:p-6">
            <CardTitle className="flex items-center justify-between text-base sm:text-lg">
              <span className="break-words">{editingId ? 'Domain Fiyatı Düzenle' : 'Yeni Domain Fiyatı Ekle'}</span>
              <Button variant="ghost" size="sm" onClick={resetForm} className="flex-shrink-0">
                <X className="w-4 h-4" />
              </Button>
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4 sm:p-6">
            <form onSubmit={handleSubmit} className="space-y-3 sm:space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Domain Uzantısı *
                  </label>
                  <div className="relative">
                    <Globe className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      type="text"
                      placeholder=".com, .net, .org..."
                      value={formData.tld}
                      onChange={(e) => setFormData({ ...formData, tld: e.target.value })}
                      className="pl-10 w-full"
                      required
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Para Birimi *
                  </label>
                  <select
                    value={formData.currency}
                    onChange={(e) => setFormData({ ...formData, currency: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                    required
                  >
                    <option value="USD">USD ($)</option>
                    <option value="TRY">TRY (₺)</option>
                    <option value="EUR">EUR (€)</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Kayıt Fiyatı *
                  </label>
                  <div className="relative">
                    <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      type="number"
                      step="0.01"
                      placeholder="11.65"
                      value={formData.registration_price}
                      onChange={(e) => setFormData({ ...formData, registration_price: e.target.value })}
                      className="pl-10 w-full"
                      required
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Orijinal Fiyat (İndirim için)
                  </label>
                  <div className="relative">
                    <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      type="number"
                      step="0.01"
                      placeholder="11.86"
                      value={formData.original_price}
                      onChange={(e) => setFormData({ ...formData, original_price: e.target.value })}
                      className="pl-10 w-full"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Yenileme Fiyatı *
                  </label>
                  <div className="relative">
                    <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      type="number"
                      step="0.01"
                      placeholder="11.86"
                      value={formData.renewal_price}
                      onChange={(e) => setFormData({ ...formData, renewal_price: e.target.value })}
                      className="pl-10 w-full"
                      required
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Transfer Fiyatı *
                  </label>
                  <div className="relative">
                    <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      type="number"
                      step="0.01"
                      placeholder="10.93"
                      value={formData.transfer_price}
                      onChange={(e) => setFormData({ ...formData, transfer_price: e.target.value })}
                      className="pl-10 w-full"
                      required
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Sıralama
                  </label>
                  <Input
                    type="number"
                    placeholder="0"
                    value={formData.sort_order}
                    onChange={(e) => setFormData({ ...formData, sort_order: e.target.value })}
                    className="w-full"
                  />
                </div>

                <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:space-x-6">
                  <label className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={formData.is_promo}
                      onChange={(e) => setFormData({ ...formData, is_promo: e.target.checked })}
                      className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 flex-shrink-0"
                    />
                    <span className="text-sm font-medium text-gray-700">Promosyon</span>
                  </label>
                  
                  <label className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={formData.is_active}
                      onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                      className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 flex-shrink-0"
                    />
                    <span className="text-sm font-medium text-gray-700">Aktif</span>
                  </label>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:space-x-3 pt-4 border-t">
                <Button type="submit" className="bg-blue-600 hover:bg-blue-700 w-full sm:w-auto">
                  <Save className="w-4 h-4 mr-2" />
                  {editingId ? 'Güncelle' : 'Ekle'}
                </Button>
                <Button type="button" variant="outline" onClick={resetForm} className="w-full sm:w-auto">
                  <X className="w-4 h-4 mr-2" />
                  İptal
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {/* Prices Table */}
      <Card>
        <CardHeader className="p-4 sm:p-6">
          <CardTitle className="text-base sm:text-lg break-words">Mevcut Domain Fiyatları ({prices.length})</CardTitle>
        </CardHeader>
        <CardContent className="p-0 sm:p-6">
          {/* Desktop Table */}
          <div className="hidden md:block overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left py-3 px-4 font-semibold text-gray-700 text-sm">Uzantı</th>
                  <th className="text-left py-3 px-4 font-semibold text-gray-700 text-sm">Kayıt</th>
                  <th className="text-left py-3 px-4 font-semibold text-gray-700 text-sm">Orijinal</th>
                  <th className="text-left py-3 px-4 font-semibold text-gray-700 text-sm">Yenileme</th>
                  <th className="text-left py-3 px-4 font-semibold text-gray-700 text-sm">Transfer</th>
                  <th className="text-left py-3 px-4 font-semibold text-gray-700 text-sm">Para Birimi</th>
                  <th className="text-center py-3 px-4 font-semibold text-gray-700 text-sm">Promosyon</th>
                  <th className="text-center py-3 px-4 font-semibold text-gray-700 text-sm">Durum</th>
                  <th className="text-center py-3 px-4 font-semibold text-gray-700 text-sm">Sıra</th>
                  <th className="text-right py-3 px-4 font-semibold text-gray-700 text-sm">İşlemler</th>
                </tr>
              </thead>
              <tbody>
                {prices.length === 0 ? (
                  <tr>
                    <td colSpan="10" className="text-center py-8 text-gray-500 text-sm">
                      Henüz domain fiyatı eklenmemiş
                    </td>
                  </tr>
                ) : (
                  prices.map((price) => (
                    <tr key={price.id} className="border-b border-gray-100 hover:bg-gray-50">
                      <td className="py-3 px-4">
                        <div className="flex items-center space-x-2">
                          <Globe className="w-4 h-4 text-blue-600 flex-shrink-0" />
                          <span className="font-semibold text-gray-900 text-sm break-words">{price.tld}</span>
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        <span className="font-medium text-green-600 text-sm">
                          {price.currency === 'USD' ? '$' : price.currency === 'TRY' ? '₺' : '€'}
                          {parseFloat(price.registration_price).toFixed(2)}
                        </span>
                      </td>
                      <td className="py-3 px-4">
                        {price.original_price ? (
                          <span className="text-gray-400 line-through text-sm">
                            {price.currency === 'USD' ? '$' : price.currency === 'TRY' ? '₺' : '€'}
                            {parseFloat(price.original_price).toFixed(2)}
                          </span>
                        ) : (
                          <span className="text-gray-400 text-sm">-</span>
                        )}
                      </td>
                      <td className="py-3 px-4 text-gray-600 text-sm">
                        {price.currency === 'USD' ? '$' : price.currency === 'TRY' ? '₺' : '€'}
                        {parseFloat(price.renewal_price).toFixed(2)}
                      </td>
                      <td className="py-3 px-4 text-gray-600 text-sm">
                        {price.currency === 'USD' ? '$' : price.currency === 'TRY' ? '₺' : '€'}
                        {parseFloat(price.transfer_price).toFixed(2)}
                      </td>
                      <td className="py-3 px-4">
                        <Badge variant="secondary" className="text-xs">{price.currency}</Badge>
                      </td>
                      <td className="py-3 px-4 text-center">
                        {price.is_promo ? (
                          <Badge className="bg-yellow-500 text-white text-xs">Promosyon</Badge>
                        ) : (
                          <span className="text-gray-400 text-sm">-</span>
                        )}
                      </td>
                      <td className="py-3 px-4 text-center">
                        <button
                          onClick={() => toggleStatus(price.id)}
                          className={`px-2 py-1 rounded-full text-xs font-semibold ${
                            price.is_active
                              ? 'bg-green-100 text-green-700'
                              : 'bg-gray-100 text-gray-700'
                          }`}
                        >
                          {price.is_active ? 'Aktif' : 'Pasif'}
                        </button>
                      </td>
                      <td className="py-3 px-4 text-center text-gray-600 text-sm">
                        {price.sort_order}
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex items-center justify-end space-x-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleEdit(price)}
                            className="text-blue-600 hover:text-blue-700"
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDelete(price.id)}
                            className="text-red-600 hover:text-red-700"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Mobile Cards */}
          <div className="md:hidden space-y-4 p-4">
            {prices.length === 0 ? (
              <div className="text-center py-8 text-gray-500 text-sm">
                Henüz domain fiyatı eklenmemiş
              </div>
            ) : (
              prices.map((price) => (
                <Card key={price.id} className="border border-gray-200">
                  <CardContent className="p-4 space-y-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <Globe className="w-5 h-5 text-blue-600 flex-shrink-0" />
                        <span className="font-semibold text-gray-900 text-base break-words">{price.tld}</span>
                      </div>
                      <Badge variant="secondary" className="text-xs">{price.currency}</Badge>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-3 text-sm">
                      <div>
                        <span className="text-gray-600 text-xs">Kayıt:</span>
                        <div className="font-medium text-green-600">
                          {price.currency === 'USD' ? '$' : price.currency === 'TRY' ? '₺' : '€'}
                          {parseFloat(price.registration_price).toFixed(2)}
                        </div>
                      </div>
                      <div>
                        <span className="text-gray-600 text-xs">Yenileme:</span>
                        <div className="font-medium text-gray-700">
                          {price.currency === 'USD' ? '$' : price.currency === 'TRY' ? '₺' : '€'}
                          {parseFloat(price.renewal_price).toFixed(2)}
                        </div>
                      </div>
                      {price.original_price && (
                        <div>
                          <span className="text-gray-600 text-xs">Orijinal:</span>
                          <div className="text-gray-400 line-through">
                            {price.currency === 'USD' ? '$' : price.currency === 'TRY' ? '₺' : '€'}
                            {parseFloat(price.original_price).toFixed(2)}
                          </div>
                        </div>
                      )}
                      <div>
                        <span className="text-gray-600 text-xs">Transfer:</span>
                        <div className="font-medium text-gray-700">
                          {price.currency === 'USD' ? '$' : price.currency === 'TRY' ? '₺' : '€'}
                          {parseFloat(price.transfer_price).toFixed(2)}
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center justify-between pt-2 border-t">
                      <div className="flex items-center gap-2">
                        {price.is_promo && (
                          <Badge className="bg-yellow-500 text-white text-xs">Promosyon</Badge>
                        )}
                        <button
                          onClick={() => toggleStatus(price.id)}
                          className={`px-2 py-1 rounded-full text-xs font-semibold ${
                            price.is_active
                              ? 'bg-green-100 text-green-700'
                              : 'bg-gray-100 text-gray-700'
                          }`}
                        >
                          {price.is_active ? 'Aktif' : 'Pasif'}
                        </button>
                        <span className="text-xs text-gray-500">Sıra: {price.sort_order}</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleEdit(price)}
                          className="text-blue-600 hover:text-blue-700"
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDelete(price.id)}
                          className="text-red-600 hover:text-red-700"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        </CardContent>
      </Card>

      {/* Info Card */}
      <Card className="bg-blue-50 border-blue-200">
        <CardContent className="p-4 sm:p-6">
          <p className="text-xs sm:text-sm text-blue-800 break-words">
            <strong>💡 Not:</strong> Domain fiyatları site ana sayfasında ve domain sorgulama sayfasında gösterilir. 
            Promosyon işareti orijinal fiyat girildiğinde otomatik olarak aktif olur.
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminDomainPrices;

